import React from 'react';
import {observer} from 'mobx-react';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import ProductRender from '../../rendering/render';
import AbTestStore, {ABTestKeys} from '../../../global/store/ab-test.store';
import {isNullOrWhiteSpace, Portal} from '../../../util';

declare const window: any;

interface SliderTHModalProps {
    onSwiperInit: (swiper: any) => void;
    onImageClick: (e: any) => void;
    showSmartBox: boolean;
}

const SliderTHModal: React.FC<SliderTHModalProps> = observer(({onSwiperInit, onImageClick, showSmartBox}) => {
    if (!s.ui.zoom_thumbnails) {
        return null;
    }

    const portalAllowsAutoSlides = Portal?.PortalID !== 3;
    const showAutoSlide1 = portalAllowsAutoSlides && !isNullOrWhiteSpace(s.prod.th_auto_2_1);
    const showAutoSlide2 = portalAllowsAutoSlides && !isNullOrWhiteSpace(s.prod.th_auto_2_2);

    return (
        <ModalSimple
            id="modal-prod-swiper-th"
            title="Product Zoom"
            open={true}
            maxWidth={'90%'}
            close={() => (s.ui.zoom_thumbnails = false)}
        >
            <Swiper
                id="prod-swiper-th-zoom"
                onInit={onSwiperInit}
                slidesPerView={1}
                centeredSlides={true}
                grabCursor={true}
                zoom={true}
                keyboard={{enabled: true}}
                loop={true}
                scrollbar={true}
                navigation={true}
                pagination={false}
                modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                slidesPerGroupAuto={true}
                aria-roledescription="carousel"
                role="region"
                aria-label="Product images"
            >
                {s.prod.CustomizationMethod === 2 && s.ready && (
                    <SwiperSlide id="slide-prod">
                        <ProductRender
                            zoom={true}
                            pcs={s.pcs}
                            css={s.ps.css_zoom}
                        />
                    </SwiperSlide>
                )}

                {s.ps.moulding_id > 0 && (
                    <SwiperSlide id="slide-prod-moulding">
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={`https://${Portal?.CDNDomain}/images/entities/moulding/${s.ui.moulding_th5 ? `${s.ps.moulding_id}_th5.webp` : s.ui.moulding_zoom_path.toLowerCase().replace('.png', '.webp')}`}
                            />
                            <img
                                src={`https://${Portal?.CDNDomain}/images/entities/moulding/${s.ui.moulding_th5 ? `${s.ps.moulding_id}_th5.png` : s.ui.moulding_zoom_path.toLowerCase()}`}
                                alt={s.ui.title_moulding}
                                onClick={onImageClick}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                            />
                        </picture>
                    </SwiperSlide>
                )}

                {s.ui.moulding_th6 && (
                    <SwiperSlide id="slide-prod-moulding-2">
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={`https://${Portal?.CDNDomain}/images/entities/moulding/${s.ps.moulding_id}_th6.webp`}
                            />
                            <img
                                src={`https://${Portal?.CDNDomain}/images/entities/moulding/${s.ps.moulding_id}_th6.png`}
                                alt={s.ui.title_moulding}
                                onClick={onImageClick}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                            />
                        </picture>
                    </SwiperSlide>
                )}

                {s.prod && s.prod.is_tassel_cord && (
                    <SwiperSlide id="slide-prod-tassel-cord">
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={`https://${Portal?.CDNDomain}/images/cached/general/tassel-cord-2-way.webp`}
                            />
                            <img
                                src={`https://${Portal?.CDNDomain}/images/cached/general/tassel-cord-2-way.png`}
                                alt={`Tassel & Cord Frame holds ${s.si.ProdInstText} graduation tassel or honor cords`}
                                onClick={onImageClick}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                            />
                        </picture>
                    </SwiperSlide>
                )}

                {(s.prod.ProductTypeID == 83 || s.prod.ProductTypeID == 84 || s.prod.ProductTypeID == 87) && !isNullOrWhiteSpace(s.prod.th_auto_2_16) && (
                    <SwiperSlide id={`slide-prod-th_auto_2_16`}>
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={s.prod.th_auto_2_16.replace('.png', '.webp').replace('.jpg', '.webp')}
                            />
                            <img
                                src={s.prod.th_auto_2_16}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #16`}
                            />
                        </picture>

                        <div className="swiper-zoom-container"></div>
                    </SwiperSlide>
                )}

                {(s.prod.ProductTypeID == 83 || s.prod.ProductTypeID == 84 || s.prod.ProductTypeID == 87) && !isNullOrWhiteSpace(s.prod.th_auto_2_17) && (
                    <SwiperSlide id={`slide-prod-th_auto_2_17`}>
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={s.prod.th_auto_2_17.replace('.png', '.webp').replace('.jpg', '.webp')}
                            />
                            <img
                                src={s.prod.th_auto_2_17}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #17`}
                            />
                        </picture>

                        <div className="swiper-zoom-container"></div>
                    </SwiperSlide>
                )}

                {showAutoSlide1 && (
                    <SwiperSlide id="slide-prod-th_auto_2_1">
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={s.prod.th_auto_2_1.replace('.png', '.webp').replace('.jpg', '.webp')}
                            />
                            <img
                                src={s.prod.th_auto_2_1}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt="Product thumbnail zoom #1"
                            />
                        </picture>
                        <div className="swiper-zoom-container"></div>
                    </SwiperSlide>
                )}

                {showAutoSlide2 && (
                    <SwiperSlide id={`slide-prod-th_auto_2_2`}>
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={s.prod.th_auto_2_2.replace('.png', '.webp').replace('.jpg', '.webp')}
                            />
                            <img
                                src={s.prod.th_auto_2_2}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #2`}
                            />
                        </picture>
                        <div className="swiper-zoom-container"></div>
                    </SwiperSlide>
                )}

                {!showAutoSlide1 && !s.prod.th5Disabled && !isNullOrWhiteSpace(s.prod.th5) && (
                    <SwiperSlide id={`slide-prod-th5`}>
                        {!s.prod.th5_special && <div className="zoom-overlay">Photo represents scale dimensions only (final art/colors may differ)</div>}
                        <div className="swiper-zoom-container">
                            <img
                                src={s.prod.th5_special ? `https://${Portal?.CDNDomain}${s.si.th_auto_2_2}` : `https://${Portal?.CDNDomain}${s.prod.th5}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #5`}
                            />
                        </div>
                    </SwiperSlide>
                )}

                {!showAutoSlide2 && !s.prod.th6Disabled && !isNullOrWhiteSpace(s.prod.th6) && (
                    <SwiperSlide id={`slide-prod-th6`}>
                        {!s.prod.th6_special && <div className="zoom-overlay">Photo represents scale dimensions only (final art/colors may differ)</div>}
                        <div className="swiper-zoom-container">
                            <img
                                src={s.prod.th6_special ? `https://${Portal?.CDNDomain}${s.si.th_auto_2_1}` : `https://${Portal?.CDNDomain}${s.prod.th6}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #6`}
                            />
                        </div>
                    </SwiperSlide>
                )}

                {!s.prod.th1Disabled && !isNullOrWhiteSpace(s.prod.th1) && (
                    <SwiperSlide id={`slide-prod-th1`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th1}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #1`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th2Disabled && !isNullOrWhiteSpace(s.prod.th2) && (
                    <SwiperSlide id={`slide-prod-th2`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th2}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #2`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th3Disabled && !isNullOrWhiteSpace(s.prod.th3) && (
                    <SwiperSlide id={`slide-prod-th3`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th3}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #3`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th4Disabled && !isNullOrWhiteSpace(s.prod.th4) && (
                    <SwiperSlide id={`slide-prod-th4`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th4}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #4`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th7Disabled && !isNullOrWhiteSpace(s.prod.th7) && (
                    <SwiperSlide id={`slide-prod-th7`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th7}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #7`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th8Disabled && !isNullOrWhiteSpace(s.prod.th8) && (
                    <SwiperSlide id={`slide-prod-th8`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th8}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #8`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th9Disabled && !isNullOrWhiteSpace(s.prod.th9) && (
                    <SwiperSlide id={`slide-prod-th9`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th9}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #9`}
                            />
                        </div>
                    </SwiperSlide>
                )}
                {!s.prod.th10Disabled && s.prod.CustomizationMethod === 2 && !isNullOrWhiteSpace(s.prod.th10) && (
                    <SwiperSlide id={`slide-prod-th10`}>
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}${s.prod.th10}_1440.jpg`}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                alt={`Product thumbnail zoom #10`}
                            />
                        </div>
                    </SwiperSlide>
                )}

                {s.ps.mat1_id > 0 && (
                    <SwiperSlide id="slide-prod-matcombo">
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={`https://${Portal?.CDNDomain}/images/entities/matcombos/1024_${s.ps.mat1_id}${s.ps.mat2_id > 0 ? `_${s.ps.mat2_id.toString()}` : ''}.webp`}
                            />
                            <img
                                src={`https://${Portal?.CDNDomain}/images/entities/matcombos/1024_${s.ps.mat1_id}${s.ps.mat2_id > 0 ? `_${s.ps.mat2_id.toString()}` : ''}.png`}
                                alt={s.ui.title_mat_combo}
                                onClick={onImageClick}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                            />
                        </picture>
                    </SwiperSlide>
                )}

                {s.ps.insignia_id > 0 && (
                    <SwiperSlide id="slide-prod-ins">
                        <div
                            style={{
                                backgroundColor: `#${s.ui.color_mat1}`,
                                backgroundRepeat: 'repeat',
                            }}
                        >
                            <picture className="swiper-zoom-container">
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/entities/insignia/v2/${s.ui.name_insignia.toLowerCase()}_original.webp`}
                                />
                                <img
                                    src={`https://${Portal?.CDNDomain}/images/entities/insignia/v2/${s.ui.name_insignia.toLowerCase()}_original.png`}
                                    style={{
                                        maxWidth: window.innerWidth * 0.8,
                                        maxHeight: window.innerHeight * 0.8,
                                    }}
                                    alt={'Open larger image of insignia'}
                                    onClick={onImageClick}
                                />
                            </picture>
                        </div>
                    </SwiperSlide>
                )}

                {s.prod.real_images_th.map(m => (
                    <SwiperSlide
                        key={m.id}
                        id={`slide-prod-real-${m.id}`}
                    >
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th1024.webp`}
                            />
                            <img
                                src={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th1024.jpg`}
                                alt="Real Image #1"
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                                onClick={onImageClick}
                            />
                        </picture>
                    </SwiperSlide>
                ))}

                {s.prod.CustomizationMethod !== 2 && s.prod.OldInsigniaThumb && s.prod.OldInsigniaThumb != '' && (
                    <SwiperSlide
                        key={43}
                        id="slide-prod-old-ins"
                    >
                        <div className="swiper-zoom-container">
                            <img
                                src={`https://${Portal?.CDNDomain}/images/products/insignia/full/${s.prod.OldInsigniaImage.toLowerCase()}`}
                                alt="Insignia"
                                onClick={onImageClick}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                    minWidth: '50%',
                                }}
                            />
                        </div>
                    </SwiperSlide>
                )}

                {showSmartBox && (
                    <SwiperSlide
                        key={42}
                        id="slide-prod-smartbox"
                    >
                        <div className="zoom-overlay">Photo represents our protective SMARTbox packaging (actual frame may differ)</div>
                        <picture className="swiper-zoom-container">
                            <source
                                type="image/webp"
                                srcSet={`https://${Portal?.CDNDomain}/images/cached/general/smartbox_1024.webp`}
                            />
                            <img
                                src={`https://${Portal?.CDNDomain}/images/cached/general/smartbox_1024.jpg`}
                                alt="SMARTbox Packaging"
                                onClick={onImageClick}
                                style={{
                                    maxWidth: window.innerWidth * 0.8,
                                    maxHeight: window.innerHeight * 0.8,
                                }}
                            />
                        </picture>
                    </SwiperSlide>
                )}

                {!AbTestStore.ABIsVariant(ABTestKeys.product_glass, 1) && s.prod && s.prod.IsFrame && s.prod.ProductID !== 84054 && s.prod.ProductID !== 70214 && !s.prod.disableGlassUpgrades && (
                    <SwiperSlide
                        key={43}
                        id="slide-prod-glassoptions"
                    >
                        {s.prod.AllowGlassID2 > 0 && (
                            <picture className="swiper-zoom-container">
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/cached/general/glass-all.webp`}
                                />
                                <img
                                    src={`https://${Portal?.CDNDomain}/images/cached/general/glass-all.png`}
                                    alt="Glass Options"
                                    onClick={onImageClick}
                                    style={{
                                        maxWidth: window.innerWidth * 0.8,
                                        maxHeight: window.innerHeight * 0.8,
                                    }}
                                />
                            </picture>
                        )}
                        {(!s.prod.AllowGlassID2 || s.prod.AllowGlassID2 < 1) && (
                            <picture className="swiper-zoom-container">
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/cached/general/glass-46.webp`}
                                />
                                <img
                                    src={`https://${Portal?.CDNDomain}/images/cached/general/glass-46.jpg`}
                                    alt="Glass Options"
                                    onClick={onImageClick}
                                    style={{
                                        maxWidth: window.innerWidth * 0.8,
                                        maxHeight: window.innerHeight * 0.8,
                                    }}
                                />
                            </picture>
                        )}
                    </SwiperSlide>
                )}
            </Swiper>
        </ModalSimple>
    );
});

export default SliderTHModal;
